'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import withDropComponent from 'LayoutBuilderUI/hoc/withDropComponent';
import { dragSection, dropSectionOnSection, dropNewImageElements, dropNewVideoElements } from 'LayoutBuilderUI/actions/layoutActions';
import { selectSection } from 'EmailEditorUI/redux/actions/LayoutActions';
import { SECTION, SAVE_SECTION, LAYOUT, ELEMENT } from 'LayoutBuilderUI/lib/DragDropConstants';
import { AsyncSectionAffordances } from 'LayoutBuilderUI/components/AsyncComponents';
import SectionToolbarContainer from 'LayoutBuilderUI/containers/SectionToolbarContainer';
import { getDropTargetPositionForHover } from 'LayoutBuilderUI/utils/DragDropUtils';
import RowDOMContainer from 'LayoutBuilderUI/containers/RowDOMContainer';
import { SnapPlaceholder } from 'LayoutBuilderUI/components/dragAndDrop/SnapPlaceholder';
import MouseEventsHandler from 'LayoutBuilderUI/components/utility/MouseEventsHandler';
import { DragSource, DropTarget } from 'react-dnd-compat';
import { NativeTypes, getEmptyImage } from 'react-dnd-ipeui-backend';
import { getLayoutLoggingTypeForSection } from 'EmailEditorUI/utils/usageTracking';
import { logInteraction } from 'LayoutBuilderUI/actions/ApiActions';
import { getMouseOverSectionId, getIsMouseOverChildElement, getMouseOverCommentableAreaId } from 'LayoutBuilderUI/selectors/userInterface';
import { sectionHasBlogRssModule as getSectionHasBlogRssModule } from 'EmailEditorUI/redux/selectors/ModuleSelectors';
import { getIsSectionBoxedHelper } from 'EmailEditorUI/redux/selectors/sectionSelectors';
import { getIsSingleColumnFullWidth, getLayoutSettings } from 'EmailEditorUI/redux/selectors/styleSettingsSelectors';
import { getSections, getSectionById } from 'EmailEditorUI/redux/selectors/sectionSelectors';
import { getHighlightedId, getSelectedId } from 'EmailEditorUI/redux/selectors/selected';
import { getIsFileManagerOpen } from 'LayoutBuilderUI/selectors/userInterface';
import { compose } from 'redux';
import { getCollaborationSidebarState } from 'ContentEditorUI/redux/selectors/collaborationSidebarSelectors';
import { getIsInMobileBreakpointMode } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { withErrorBoundaryConnect, ModuleRenderError } from 'EmailEditorUI/hoc/withErrorBoundary';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const sectionSource = {
  beginDrag(props, monitor, component) {
    props.dragSection();
    return {
      id: props.section.get('id'),
      containsBlogRss: props.sectionHasBlogRssModule,
      index: props.index,
      component,
      shouldUsePreview: true
    };
  },
  endDrag(props, monitor) {
    if (!monitor.didDrop()) {
      return;
    }
    const item = monitor.getItem();
    const result = monitor.getDropResult();
    if (result.type === SECTION) {
      // don't drop on itself
      if (!result.forceDropOutsideBoxed && result.sectionIndex === props.index) {
        return;
      }
      logInteraction({
        action: 'Modify Layout Action',
        event: 'move-section',
        'section-layout': getLayoutLoggingTypeForSection(props.section)
      });
      props.dropSectionOnSection({
        sectionId: item.id,
        targetSectionIndex: result.sectionIndex,
        dropSectionIndex: props.index,
        dropPosition: result.dropPosition,
        forceDropOutsideBoxed: result.forceDropOutsideBoxed
      });
    } else if (result.type !== SAVE_SECTION) {
      console.error(`Unexpected section drop on target type ${result.type}`);
    }
  }
};
const sectionTarget = {
  drop(props, monitor) {
    const itemType = monitor.getItemType();
    const isElement = itemType === ELEMENT;
    const isLayout = itemType === LAYOUT;
    const isFile = itemType === NativeTypes.FILE;
    const droppedOnChild = monitor.didDrop();
    const {
      section
    } = props;
    if (isFile || isElement || isLayout) {
      if (droppedOnChild) {
        return undefined;
      }
    }
    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    // Determine rectangle on screen
    const targetBoundingRect = props.getRect();
    const dropTargetPosition = getDropTargetPositionForHover(targetBoundingRect, clientOffset);
    const dropProps = {
      sectionId: section.get('id'),
      sectionIndex: props.index,
      dropPosition: dropTargetPosition,
      type: SECTION
    };
    if (isFile) {
      const item = monitor.getItem();
      const files = item.files && item.files.length ? item.files : null;
      if (files && files.length) {
        const imageFiles = files.filter(f => f.type.startsWith('image') && !f.type.includes('svg'));
        const videoFiles = files.filter(f => f.type.startsWith('video'));
        if (imageFiles.length) {
          props.dropNewImageElements(Object.assign({
            files: imageFiles
          }, dropProps));
        }
        if (videoFiles.length) {
          props.dropNewVideoElements(Object.assign({
            files: videoFiles
          }, dropProps));
        }
      }
      return undefined;
    }

    // Defines payload for monitor.getDropResult() in endDrag()
    return dropProps;
  },
  hover(props, monitor) {
    // Determine mouse position
    const clientOffset = monitor.getClientOffset();
    // Determine rectangle on screen
    const targetBoundingRect = props.getRect();
    const dropTargetPosition = getDropTargetPositionForHover(targetBoundingRect, clientOffset);
    props.updateSnapPlaceholderPosition(dropTargetPosition);
  },
  canDrop: props => !props.isFileManagerOpen
};
const mapStateToProps = (state, ownProps) => {
  const id = ownProps.sectionId;
  const section = getSectionById(state, id);
  if (!section) {
    return {};
  }
  const mouseIsOverChildElement = getIsMouseOverChildElement(state, id);
  const isSingleColumnFullWidth = getIsSingleColumnFullWidth(state);
  const isMobileBreakpointMode = getIsInMobileBreakpointMode(state);
  const selectedItemId = getSelectedId(state);
  const isSelected = selectedItemId === ownProps.index;
  const isDraggableNow = ownProps.draggable && typeof selectedItemId === 'undefined' && !mouseIsOverChildElement && !isSingleColumnFullWidth;
  return {
    isFakeHighlighted: getHighlightedId(state) === id,
    section,
    isBoxed: getIsSectionBoxedHelper(getLayoutSettings(state), ownProps.index),
    isDraggableNow,
    isSelected,
    mouseIsOverChildElement,
    isSingleColumnFullWidth,
    otherItemSelected: !!selectedItemId && !isSelected,
    mouseIsOver: getMouseOverSectionId(state) === id,
    numSections: getSections(state).size,
    sectionHasBlogRssModule: getSectionHasBlogRssModule(state, section),
    isFileManagerOpen: getIsFileManagerOpen(state),
    canResize: !isSingleColumnFullWidth,
    isCommentModeActive: getCollaborationSidebarState(state).isCommentModeActive,
    isMobileBreakpointMode,
    isOverCommentableArea: getMouseOverCommentableAreaId(state)
  };
};
const mapDispatchToProps = {
  selectSection,
  dragSection,
  dropSectionOnSection,
  dropNewVideoElements,
  dropNewImageElements
};
class Section extends Component {
  // ID should be generated via schema, use date.now() until then.

  constructor(props) {
    super(props);
    this.handleMouseDown = () => {
      this._mouseDown = true;
    };
    this.handleMouseUp = e => {
      if (this._mouseDown) {
        this.handleRowSelect(e);
      }
      this._mouseDown = false;
    };
    this.handleRowSelect = () => {
      const {
        index,
        selectable,
        onSelect,
        isSelected,
        isCommentModeActive,
        isOverCommentableArea
      } = this.props;
      if (!selectable || isCommentModeActive || isOverCommentableArea) {
        return;
      }
      if (typeof onSelect === 'function') {
        onSelect();
        return;
      }
      this.props.selectSection(index, !isSelected);
    };
    this.state = {
      isResizing: false,
      isHoveringSubSection: false
    };
    this.setIsHoveringSubSection = this.setIsHoveringSubSection.bind(this);
  }
  componentDidMount() {
    this.props.connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  }
  setIsHoveringSubSection(value) {
    this.setState({
      isHoveringSubSection: value
    });
  }
  renderAffordances(draggable) {
    const {
      isSelected,
      section,
      index,
      toolbar,
      otherItemSelected,
      isDragging,
      mouseIsOver,
      mouseIsOverChildElement,
      isFakeHighlighted,
      isMobileBreakpointMode
    } = this.props;
    const selectedOrHovered = mouseIsOver || isSelected;
    const shouldHideAffordances = otherItemSelected || !selectedOrHovered || !(isSelected || mouseIsOver || mouseIsOverChildElement);
    if (!isFakeHighlighted && shouldHideAffordances) {
      return null;
    }
    return /*#__PURE__*/_jsx(AsyncSectionAffordances, {
      isSelected: isSelected,
      isOver: mouseIsOver || isFakeHighlighted,
      isOverChildElement: mouseIsOverChildElement,
      isDragging: isDragging,
      draggable: draggable,
      "data-test": "section-affordances",
      isMobileBreakpointMode: isMobileBreakpointMode,
      children: (toolbar !== false || isFakeHighlighted) && /*#__PURE__*/_jsx(SectionToolbarContainer, {
        sectionId: section.get('id'),
        index: index,
        handleEdit: this.handleRowSelect,
        isOverChildElement: mouseIsOverChildElement,
        containerRef: this.node
      })
    });
  }
  render() {
    const {
      section,
      index,
      isSelected,
      isOver,
      isDraggableNow,
      selectable,
      isDragging,
      connectDragSource,
      connectDropTarget,
      snapPlaceholderPosition,
      isBoxed,
      numSections,
      isSingleColumnFullWidth,
      mouseIsOver,
      otherItemSelected,
      canResize,
      mouseIsOverChildElement,
      isCommentModeActive
    } = this.props;
    const isSnapPlaceholderVisible = Boolean(isOver && snapPlaceholderPosition);
    const sectionClasses = classNames('cos-section', selectable && "selectable", isSingleColumnFullWidth && "fullWidthSection", isSelected && "selected", isDraggableNow && "draggable", isDragging && 'is-dragging', isBoxed && "boxed", canResize && 'resizable-section', this.state.isHoveringSubSection && 'hover-on-subsection');
    const content =
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized */
    _jsx("section", {
      className: sectionClasses,
      onMouseDown: this.handleMouseDown,
      onMouseUp: this.handleMouseUp,
      onDragEnter: this.props.onDragEnter,
      ref: ref => {
        this.props.getElementRef(ref);
        this.node = ref;
      },
      id: `section-${section && section.get('id')}-container`,
      children: /*#__PURE__*/_jsxs(MouseEventsHandler, {
        type: section && SECTION,
        id: section && section.get('id'),
        isSelected: isSelected,
        children: [/*#__PURE__*/_jsx(SnapPlaceholder, {
          isVisible: isSnapPlaceholderVisible,
          position: snapPlaceholderPosition
        }), /*#__PURE__*/_jsx(RowDOMContainer, {
          isDragging: isDragging,
          section: section,
          selectable: selectable,
          isSelected: isSelected,
          hovered: mouseIsOver && !isOver && !otherItemSelected,
          index: index,
          numSections: numSections,
          isSingleColumnFullWidth: isSingleColumnFullWidth,
          startResize: this.startResize,
          containerRef: this.node,
          isOverChildElement: mouseIsOverChildElement,
          setIsHoveringSubSection: this.setIsHoveringSubSection,
          children: this.props.children
        }), this.renderAffordances(isDraggableNow)]
      })
    });
    if (!isDraggableNow || isCommentModeActive) {
      return connectDropTarget(content);
    }
    return connectDragSource(connectDropTarget(content));
  }
}
Section.propTypes = {
  index: PropTypes.number,
  section: PropTypes.object,
  selectable: PropTypes.bool,
  children: PropTypes.node,
  isSelected: PropTypes.bool,
  isDraggableNow: PropTypes.bool,
  otherItemSelected: PropTypes.bool,
  toolbar: PropTypes.bool,
  onSelect: PropTypes.func,
  isBoxed: PropTypes.bool,
  mouseIsOver: PropTypes.bool,
  mouseIsOverChildElement: PropTypes.bool,
  selectSection: PropTypes.func,
  snapPlaceholderPosition: PropTypes.string,
  numSections: PropTypes.number,
  isSingleColumnFullWidth: PropTypes.bool.isRequired,
  canResize: PropTypes.bool.isRequired,
  isFakeHighlighted: PropTypes.bool,
  isCommentModeActive: PropTypes.bool,
  isMobileBreakpointMode: PropTypes.bool,
  // withDropComponent props
  getElementRef: PropTypes.func,
  onDragEnter: PropTypes.func,
  // Drag/Drop
  connectDragSource: PropTypes.func,
  connectDropTarget: PropTypes.func,
  isDragging: PropTypes.bool,
  isOver: PropTypes.bool,
  // Drag Layer
  connectDragPreview: PropTypes.func
};
Section.defaultProps = {
  selectable: true,
  isSelected: false,
  draggable: false
};
export default compose(connect(mapStateToProps, mapDispatchToProps), withDropComponent, DragSource(SECTION, sectionSource, monitor => ({
  isDragging: monitor.isDragging()
})), DropTarget([SECTION, ELEMENT, LAYOUT, NativeTypes.FILE, SAVE_SECTION], sectionTarget, monitor => {
  const itemType = monitor.getItemType();
  return {
    isOver: monitor.isOver({
      shallow: itemType !== SECTION && itemType !== LAYOUT
    })
  };
}), withErrorBoundaryConnect(true, /*#__PURE__*/_jsx(ModuleRenderError, {})))(Section);